import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Users } from 'lucide-react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import LoginPage from './pages/LoginPage.js';
import Dashboard from './pages/Dashboard.js';
import UsersManagement from './pages/UsersManagement.js';
import LiveSessions from './pages/LiveSessions.js';
import GiftsManagement from './pages/GiftsManagement.js';
import WalletManagement from './pages/WalletManagement.js';
import Reports from './pages/Reports.js';
import MomentsManagement from './pages/MomentsManagement.js';
import AgenciesManagement from './pages/AgenciesManagement.js';
import AgencyDetails from './pages/AgencyDetails.js';
import BDManagement from './pages/BDManagement.js';
import OrganizationHub from './pages/OrganizationHub.js';
import AgencyLogin from './pages/AgencyLogin.js';
import AgencyDashboard from './pages/AgencyDashboard.js';
import AgencyHosts from './pages/AgencyHosts.js';
import AgencyRecharge from './pages/AgencyRecharge.js';
import AgencyWallet from './pages/AgencyWallet.js';
import AgencySupport from './pages/AgencySupport.js';
import WithdrawalManagement from './pages/WithdrawalManagement.js';
import Layout from './components/Layout.js';
import AgencyLayout from './components/AgencyLayout.js';
import SellerLayout from './components/SellerLayout.js';
import SellerLogin from './pages/SellerLogin.js';
import SellerDashboard from './pages/SellerDashboard.js';
import SellerChats from './pages/SellerChats.js';
import ProtectedRoute from './components/ProtectedRoute.js';

// Business Development (BD) Portal
import BDLayout from './components/BDLayout.js';
import BDLogin from './pages/BDLogin.js';
import BDDashboard from './pages/BDDashboard.js';
import BDAgencies from './pages/BDAgencies.js';
import BDHosts from './pages/BDHosts.js';
import BDPerformance from './pages/BDPerformance.js';
import BDSettings from './pages/BDSettings.js';
import BDSupport from './pages/BDSupport.js';

// Executive Manager Portal
import ManagerLayout from './components/ManagerLayout.js';
import ManagerLogin from './pages/ManagerLogin.js';
import ManagerDashboard from './pages/ManagerDashboard.js';
import ManagerBDs from './pages/ManagerBDs.js';
import ManagerAgencies from './pages/ManagerAgencies.js';
import ManagerHosts from './pages/ManagerHosts.js';
import ManagerPerformance from './pages/ManagerPerformance.js';
import ManagerSettings from './pages/ManagerSettings.js';
import ManagerSupport from './pages/ManagerSupport.js';
import ManagerUsers from './pages/ManagerUsers.js';
import ManagerRooms from './pages/ManagerRooms.js';
import ManagerStore from './pages/ManagerStore.js';
import ManagerSalaries from './pages/ManagerSalaries.js';
import ManagerAuditLogs from './pages/ManagerAuditLogs.js';

// New Admin Panel Modules
import TargetsConfiguration from './pages/TargetsConfiguration.js';
import HostManagement from './pages/HostManagement.js';
import RewardsTracking from './pages/RewardsTracking.js';
import CommissionsTracking from './pages/CommissionsTracking.js';
import StoreManagement from './pages/StoreManagement.js';
import FeedbackManagement from './pages/FeedbackManagement.js';
import LevelSettings from './pages/LevelSettings.js';

import SecurityLogs from './pages/SecurityLogs.js';
import SellersManagement from './pages/SellersManagement.js';
import RechargeManagement from './pages/RechargeManagement.js';
import CoinManagement from './pages/CoinManagement.js';
import CommissionManagement from './pages/CommissionManagement.js';
import FraudControl from './pages/FraudControl.js';
import SupportCenter from './pages/SupportCenter.js';
import SettingsPanel from './pages/SettingsPanel.js';
import PolicyManagement from './pages/PolicyManagement.js';
import AdminRoles from './pages/AdminRoles.js';
import PermissionsManagement from './pages/PermissionsManagement.js';
import SystemMonitor from './pages/SystemMonitor.js';
import MaintenancePanel from './pages/MaintenancePanel.js';
import AgencyStructure from './pages/AgencyStructure.js';
import NotificationsManagement from './pages/NotificationsManagement.js';
import NewsManagement from './pages/NewsManagement.js';
import SecretSystemClean from './pages/SecretSystemClean.js';
import BannersManagement from './pages/BannersManagement.js';
import RoomThemesManagement from './pages/RoomThemesManagement.js';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#e57373',
    },
    background: {
      default: '#f5f5f5',
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <Dashboard />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/security-logs" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <SecurityLogs />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/users" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <UsersManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/sellers" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <SellersManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/recharge" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <RechargeManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/wallet" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <WalletManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/withdrawals" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <WithdrawalManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/withdrawal-requests" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <WithdrawalManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/coins" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <CoinManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/commissions" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <CommissionManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/targets-config" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <TargetsConfiguration />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/level-settings" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <LevelSettings />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/hosts-management" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <HostManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/store-management" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <StoreManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/rewards-tracking" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <RewardsTracking />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/commissions-tracking" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <CommissionsTracking />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/fraud-control" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <FraudControl />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/live-sessions" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <LiveSessions />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/gifts" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <GiftsManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <Reports />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/support" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <SupportCenter />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/app-feedback" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <FeedbackManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/notifications" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <NotificationsManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <SettingsPanel />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/policies" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <PolicyManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/roles" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <AdminRoles />
              </Layout>
            </ProtectedRoute>
          } />
          {/* Aliases / Redirects for Roles & Rooms */}
          <Route path="/admin-roles" element={<Navigate to="/roles" replace />} />
          <Route path="/super-admin" element={<Navigate to="/roles" replace />} />
          <Route path="/super-admins" element={<Navigate to="/roles" replace />} />
          <Route path="/rooms" element={<Navigate to="/live-sessions" replace />} />
          <Route path="/room-management" element={<Navigate to="/live-sessions" replace />} />
          <Route path="/permissions" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <PermissionsManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/banners" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <BannersManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/room-themes" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <RoomThemesManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/system-monitor" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <SystemMonitor />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/maintenance" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <MaintenancePanel />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/moments" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <MomentsManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/organization" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <OrganizationHub />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/agencies" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <OrganizationHub />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/bd-management" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <OrganizationHub />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/agencies-list" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <AgenciesManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/agencies/:id" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <AgencyDetails />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/bd-management" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <BDManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/agency-structure" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <AgencyStructure />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/news" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <Layout>
                <NewsManagement />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Agency Panel Routes */}
          <Route path="/agency/login" element={<AgencyLogin />} />
          <Route path="/agency/dashboard" element={
            <ProtectedRoute tokenKey="agencyToken" redirectPath="/agency/login">
              <AgencyLayout>
                <AgencyDashboard />
              </AgencyLayout>
            </ProtectedRoute>
          } />
          <Route path="/agency/recharge" element={
            <ProtectedRoute tokenKey="agencyToken" redirectPath="/agency/login">
              <AgencyLayout>
                <AgencyRecharge />
              </AgencyLayout>
            </ProtectedRoute>
          } />
          <Route path="/agency/hosts" element={
            <ProtectedRoute tokenKey="agencyToken" redirectPath="/agency/login">
              <AgencyLayout>
                <AgencyHosts />
              </AgencyLayout>
            </ProtectedRoute>
          } />
          <Route path="/agency/wallet" element={
            <ProtectedRoute tokenKey="agencyToken" redirectPath="/agency/login">
              <AgencyLayout>
                <AgencyWallet />
              </AgencyLayout>
            </ProtectedRoute>
          } />
          <Route path="/agency/support" element={
            <ProtectedRoute tokenKey="agencyToken" redirectPath="/agency/login">
              <AgencyLayout>
                <AgencySupport />
              </AgencyLayout>
            </ProtectedRoute>
          } />

          {/* Coin Seller Panel Routes */}
          <Route path="/seller/login" element={<SellerLogin />} />
          <Route path="/seller/dashboard" element={
            <ProtectedRoute tokenKey="sellerToken" redirectPath="/seller/login">
              <SellerLayout>
                <SellerDashboard />
              </SellerLayout>
            </ProtectedRoute>
          } />
          <Route path="/seller/wallet" element={
            <ProtectedRoute tokenKey="sellerToken" redirectPath="/seller/login">
              <SellerLayout>
                <SellerDashboard />
              </SellerLayout>
            </ProtectedRoute>
          } />
          <Route path="/seller/history" element={
            <ProtectedRoute tokenKey="sellerToken" redirectPath="/seller/login">
              <SellerLayout>
                <SellerDashboard />
              </SellerLayout>
            </ProtectedRoute>
          } />
          <Route path="/seller/chats" element={
            <ProtectedRoute tokenKey="sellerToken" redirectPath="/seller/login">
              <SellerLayout>
                <SellerChats />
              </SellerLayout>
            </ProtectedRoute>
          } />

          {/* Business Development (BD) Portal Routes */}
          <Route path="/bd/login" element={<BDLogin />} />
          <Route path="/bd/dashboard" element={
            <ProtectedRoute tokenKey="bdToken" redirectPath="/bd/login">
              <BDLayout>
                <BDDashboard />
              </BDLayout>
            </ProtectedRoute>
          } />
          <Route path="/bd/agencies" element={
            <ProtectedRoute tokenKey="bdToken" redirectPath="/bd/login">
              <BDLayout>
                <BDAgencies />
              </BDLayout>
            </ProtectedRoute>
          } />
          <Route path="/bd/hosts" element={
            <ProtectedRoute tokenKey="bdToken" redirectPath="/bd/login">
              <BDLayout>
                <BDHosts />
              </BDLayout>
            </ProtectedRoute>
          } />
          <Route path="/bd/performance" element={
            <ProtectedRoute tokenKey="bdToken" redirectPath="/bd/login">
              <BDLayout>
                <BDPerformance />
              </BDLayout>
            </ProtectedRoute>
          } />
          <Route path="/bd/settings" element={
            <ProtectedRoute tokenKey="bdToken" redirectPath="/bd/login">
              <BDLayout>
                <BDSettings />
              </BDLayout>
            </ProtectedRoute>
          } />
          <Route path="/bd/support" element={
            <ProtectedRoute tokenKey="bdToken" redirectPath="/bd/login">
              <BDLayout>
                <BDSupport />
              </BDLayout>
            </ProtectedRoute>
          } />

          {/* Executive Manager Portal Routes */}
          <Route path="/manager/login" element={<ManagerLogin />} />
          <Route path="/manager/dashboard" element={
            <ProtectedRoute tokenKey="managerToken" redirectPath="/manager/login">
              <ManagerLayout>
                <ManagerDashboard />
              </ManagerLayout>
            </ProtectedRoute>
          } />
          <Route path="/manager/bds" element={
            <ProtectedRoute tokenKey="managerToken" redirectPath="/manager/login">
              <ManagerLayout>
                <ManagerBDs />
              </ManagerLayout>
            </ProtectedRoute>
          } />
          <Route path="/manager/agencies" element={
            <ProtectedRoute tokenKey="managerToken" redirectPath="/manager/login">
              <ManagerLayout>
                <ManagerAgencies />
              </ManagerLayout>
            </ProtectedRoute>
          } />
          <Route path="/manager/hosts" element={
            <ProtectedRoute tokenKey="managerToken" redirectPath="/manager/login">
              <ManagerLayout>
                <ManagerHosts />
              </ManagerLayout>
            </ProtectedRoute>
          } />
          <Route path="/manager/users" element={
            <ProtectedRoute tokenKey="managerToken" redirectPath="/manager/login">
              <ManagerLayout>
                <ManagerUsers />
              </ManagerLayout>
            </ProtectedRoute>
          } />
          <Route path="/manager/rooms" element={
            <ProtectedRoute tokenKey="managerToken" redirectPath="/manager/login">
              <ManagerLayout>
                <ManagerRooms />
              </ManagerLayout>
            </ProtectedRoute>
          } />
          <Route path="/manager/items" element={
            <ProtectedRoute tokenKey="managerToken" redirectPath="/manager/login">
              <ManagerLayout>
                <ManagerStore />
              </ManagerLayout>
            </ProtectedRoute>
          } />
          <Route path="/manager/salaries" element={
            <ProtectedRoute tokenKey="managerToken" redirectPath="/manager/login">
              <ManagerLayout>
                <ManagerSalaries />
              </ManagerLayout>
            </ProtectedRoute>
          } />
          <Route path="/manager/audit-logs" element={
            <ProtectedRoute tokenKey="managerToken" redirectPath="/manager/login">
              <ManagerLayout>
                <ManagerAuditLogs />
              </ManagerLayout>
            </ProtectedRoute>
          } />
          <Route path="/manager/performance" element={
            <ProtectedRoute tokenKey="managerToken" redirectPath="/manager/login">
              <ManagerLayout>
                <ManagerPerformance />
              </ManagerLayout>
            </ProtectedRoute>
          } />
          <Route path="/manager/settings" element={
            <ProtectedRoute tokenKey="managerToken" redirectPath="/manager/login">
              <ManagerLayout>
                <ManagerSettings />
              </ManagerLayout>
            </ProtectedRoute>
          } />
          <Route path="/manager/support" element={
            <ProtectedRoute tokenKey="managerToken" redirectPath="/manager/login">
              <ManagerLayout>
                <ManagerSupport />
              </ManagerLayout>
            </ProtectedRoute>
          } />

          {/* Secret System Reset Route (Hidden from all navigation menus) */}
          <Route path="/secret-system-reset-xoxo" element={
            <ProtectedRoute tokenKey="adminToken" redirectPath="/login">
              <SecretSystemClean />
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;